import { useSession } from 'next-auth/react'
import { type FC, type PropsWithChildren, useMemo } from 'react'
import { AdvertisingProvider } from 'react-advertising'

import { ALL_CANADA_LOCATION_ID } from '@/constants/location'
import { useGetHomeFeedAdsQuery, useGetLocationPathFromIdQuery } from '@/generated/graphql-types'
import useCookiePreferences from '@/hooks/useCookiePreferences'
import { useLocale } from '@/hooks/useLocale'
import { isLocationPath } from '@/types/homepage'

import { AdobeAudienceManager } from '../components/adobe-audience-manager'
import { AmazonTamScripts } from '../components/amazon-tam/amazonTamScripts'
import { prebidConfig } from '../components/prebid/prebidConfig'
import { PrebidScripts } from '../components/prebid/PrebidScripts'
import { getIsAuthenticatedForGpt } from '../utils/gptTargeting'
import { createHomepageGptConfig } from './createHomepageGptConfig'
import { generateGptTargetingValues } from './generateGptTargetingValues'
import { HomepageGptScripts } from './HomepageGptScripts'

type HomepageAdsWrapperProps = PropsWithChildren & {
  locationId?: number
  shouldShowHomepageFeed: boolean
}

export const HomepageAdsWrapper: FC<HomepageAdsWrapperProps> = ({
  locationId = ALL_CANADA_LOCATION_ID,
  shouldShowHomepageFeed,
  children,
}) => {
  const { routeLocale } = useLocale()
  const { status } = useSession()
  const isSignedIn = getIsAuthenticatedForGpt(status)
  const { hasUserOptedOutTargeting = false } = useCookiePreferences()

  const { data: homeFeedAdsData } = useGetHomeFeedAdsQuery()
  const numOfHomeFeedAds = homeFeedAdsData?.homeFeedAds.adSlots || 0

  const isLocationIdDefined = typeof locationId !== 'undefined'
  const { loading, data: locationData } = useGetLocationPathFromIdQuery({
    variables: {
      locationId: locationId,
    },
    /**
     * Skip if location is undefined or 0 (Canada)
     * For Canada, we only need the locationId, which is already defined
     */
    skip: !locationId,
  })

  const locationPaths = isLocationPath(locationData?.location?.locationPaths)
    ? locationData?.location?.locationPaths
    : undefined

  const gptTargetingValues = useMemo(
    () =>
      generateGptTargetingValues(
        isSignedIn,
        routeLocale,
        locationId,
        locationPaths,
        hasUserOptedOutTargeting
      ),
    [isSignedIn, routeLocale, locationId, locationPaths, hasUserOptedOutTargeting]
  )

  const homepageGptConfig = useMemo(
    () =>
      createHomepageGptConfig(
        gptTargetingValues,
        routeLocale,
        shouldShowHomepageFeed,
        numOfHomeFeedAds
      ),
    [gptTargetingValues, routeLocale, shouldShowHomepageFeed, numOfHomeFeedAds]
  )

  /**
   * We need to make React-Advertising wait for the GPT scripts to be loaded
   * Otherwise it will throw a window.googletag.cmd undefined error
   */
  return (
    <>
      <HomepageGptScripts>
        {(isReady) => (
          <AdvertisingProvider
            config={isReady && isLocationIdDefined && !loading ? homepageGptConfig : null}
            plugins={prebidConfig}
          >
            {children}
          </AdvertisingProvider>
        )}
      </HomepageGptScripts>
      <PrebidScripts />
      <AmazonTamScripts />
      <AdobeAudienceManager gpt={gptTargetingValues} />
    </>
  )
}
